import { useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@/lib/zodcompat'
import { toast } from 'sonner'
import { Plus, Lock } from 'lucide-react'
import { Dialog } from '@base-ui/react/dialog'
import { cashCategorySchema, type CashCategoryFormData } from '@/validators/cash'
import {
  useCashCategories,
  useCreateCashCategory,
  useUpdateCashCategory,
  useDeactivateCashCategory,
  useActivateCashCategory,
} from '@/hooks/useCashCategories'
import { DataTable } from '@/components/ui/data-table'
import { StatusBadge } from '@/components/ui/status-badge'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/ui/loading-button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { CashCategory } from '@/types/cash'
import type { Column } from '@/components/ui/data-table'

const selectClass = cn(
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  'disabled:cursor-not-allowed disabled:opacity-50',
)

function apiError(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? fallback
  )
}

function KindBadge({ kind }: Readonly<{ kind: CashCategory['kind'] }>) {
  return (
    <Badge tone={kind === 'INFLOW' ? 'success' : 'danger'}>
      {kind === 'INFLOW' ? 'Receita' : 'Despesa'}
    </Badge>
  )
}

interface CategoryModalProps {
  category: CashCategory | null
  open: boolean
  onClose: () => void
}

function CategoryModal({ category, open, onClose }: CategoryModalProps) {
  const isEdit = !!category
  const createMutation = useCreateCashCategory()
  const updateMutation = useUpdateCashCategory(category?.id ?? '')
  const deactivateMutation = useDeactivateCashCategory()
  const activateMutation = useActivateCashCategory()
  const mutation = isEdit ? updateMutation : createMutation

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<CashCategoryFormData>({
    resolver: zodResolver(cashCategorySchema),
    defaultValues: { name: '', kind: 'OUTFLOW' },
  })

  useEffect(() => {
    if (open) {
      reset(category ? { name: category.name, kind: category.kind } : { name: '', kind: 'OUTFLOW' })
    }
  }, [open, category, reset])

  async function onSubmit(data: CashCategoryFormData) {
    try {
      if (isEdit) {
        await updateMutation.mutateAsync({ name: data.name })
        toast.success('Categoria atualizada!')
      } else {
        await createMutation.mutateAsync(data)
        toast.success('Categoria criada!')
      }
      onClose()
    } catch (err) {
      toast.error(apiError(err, 'Erro ao salvar categoria'))
    }
  }

  async function handleToggleStatus() {
    if (!category) return
    try {
      if (category.active) {
        await deactivateMutation.mutateAsync(category.id)
        toast.success('Categoria desativada')
      } else {
        await activateMutation.mutateAsync(category.id)
        toast.success('Categoria reativada')
      }
      onClose()
    } catch (err) {
      toast.error(apiError(err, 'Erro ao alterar status'))
    }
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose()
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-40 bg-black/50" />
        <Dialog.Popup className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background p-6 shadow-md">
          <Dialog.Title className="text-lg font-semibold text-foreground">
            {isEdit ? 'Editar categoria' : 'Nova categoria'}
          </Dialog.Title>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="cat-name">Nome *</Label>
              <Input id="cat-name" aria-invalid={!!errors.name} {...register('name')} />
              {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cat-kind">Tipo *</Label>
              <Controller
                name="kind"
                control={control}
                render={({ field }) => (
                  <select
                    id="cat-kind"
                    value={field.value}
                    onChange={field.onChange}
                    disabled={isEdit}
                    className={selectClass}
                  >
                    <option value="INFLOW">Receita (entrada)</option>
                    <option value="OUTFLOW">Despesa (saída)</option>
                  </select>
                )}
              />
              {isEdit && (
                <p className="text-xs text-muted-foreground">O tipo não pode ser alterado.</p>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 pt-2">
              <div>
                {isEdit && (
                  <Button
                    type="button"
                    variant={category.active ? 'destructive' : 'outline'}
                    onClick={handleToggleStatus}
                    disabled={deactivateMutation.isPending || activateMutation.isPending}
                  >
                    {category.active ? 'Desativar' : 'Reativar'}
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={onClose} disabled={mutation.isPending}>
                  Cancelar
                </Button>
                <LoadingButton type="submit" loading={mutation.isPending} loadingText="Salvando…">
                  {isEdit ? 'Salvar' : 'Criar'}
                </LoadingButton>
              </div>
            </div>
          </form>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

const COLUMNS: Column<CashCategory>[] = [
  {
    key: 'name',
    header: 'Categoria',
    render: (row) => (
      <span className="flex items-center gap-1.5">
        {row.name}
        {row.isSystem && (
          <span title="Categoria do sistema" className="text-muted-foreground">
            <Lock size={12} />
          </span>
        )}
      </span>
    ),
  },
  { key: 'kind', header: 'Tipo', render: (row) => <KindBadge kind={row.kind} /> },
  {
    key: 'active',
    header: 'Status',
    render: (row) => <StatusBadge active={row.active} />,
  },
]

export function CashCategoryListPage() {
  const { data, isLoading } = useCashCategories({ perPage: 100 })
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<CashCategory | null>(null)

  function openCreate() {
    setEditing(null)
    setModalOpen(true)
  }

  function openEdit(category: CashCategory) {
    if (category.isSystem) {
      toast.info('Categorias do sistema não podem ser editadas')
      return
    }
    setEditing(category)
    setModalOpen(true)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Categorias</h1>
          <p className="text-sm text-muted-foreground">
            Classificação de receitas e despesas no fluxo de caixa
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus size={16} />
          Nova categoria
        </Button>
      </div>

      <DataTable
        columns={COLUMNS}
        data={data?.data ?? []}
        keyExtractor={(row) => row.id}
        onRowClick={openEdit}
        isLoading={isLoading}
        emptyTitle="Nenhuma categoria cadastrada"
        emptyDescription="Crie categorias para classificar os lançamentos."
        emptyAction={
          <Button onClick={openCreate}>
            <Plus size={16} />
            Nova categoria
          </Button>
        }
      />

      <CategoryModal category={editing} open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  )
}
